import { AppRouter } from "./components";
import "./index.css";
import { lazy, Suspense, useEffect, useMemo } from "react";
import { initScreenWidth } from "./utils/screenWidth";
import { useFavoriteGames } from "./hooks/useFavoriteGames";
import { useIOSKeyboardFix } from "./hooks/useIOSKeyboardFix";
import { useUserDataFlattened } from "@/modules/user/stores";
import { PopupQueueProvider, FloatingIconsProvider } from "./contexts";
import { useCustomerServices } from "./modules/homeDisplay/hooks";
import { AgreementConfirmation } from "./components/AgreementConfirmation";
import { UnpaidGuideController } from "./components/UnpaidGuideModal";
import { LoginRegisterPopup } from "./components/LoginRegisterPopup";
import { DepositPopup } from "./components/DepositPopup";
import { WithdrawPopup } from "./components/WithdrawPopup";
import { useGlobalValue } from "./stores/GlobalState";
import BlockedDevicePage from "./pages/BlockedDevice";
import { SilentErrorBoundary } from "./components/ChunkLoadBoundary/ChunkLoadBoundary";
import "./utils/imageReloadManager";
import { APP_GLOBAL_EVENT_FLOATING_VISIBILITY } from "./components/Event";

const LeftBottomFloatingIcons = lazy(() =>
  import("./components/LeftBottomFloatingIcons").then(m => ({
    default: m.LeftBottomFloatingIcons,
  }))
);
const TawkToChat = lazy(() =>
  import("./components/TawkToChat").then(m => ({ default: m.TawkToChat }))
);
const LiveChat = lazy(() =>
  import("./components/LiveChat").then(m => ({ default: m.LiveChat }))
);
const GlobalSafariAddHomeScreen = lazy(() =>
  import("./pages/LandingPage/components/SafariAddHomeScreen").then(m => ({
    default: m.GlobalSafariAddHomeScreen,
  }))
);
const GlobalDownloadAppPopup = lazy(() =>
  import("./components/DownloadAppPopup/GlobalDownloadAppPopup").then(m => ({
    default: m.GlobalDownloadAppPopup,
  }))
);
const GlobalAppDownloadDialog = lazy(() =>
  import("./components/AppDownloadDialog").then(m => ({
    default: m.GlobalAppDownloadDialog,
  }))
);
const WebPush = lazy(() => import("./components/WebPush"));
const GlobalEvent = lazy(() =>
  import("./components/Event").then(m => ({ default: m.GlobalEvent }))
);
const GlobalRulesDialog = lazy(() =>
  import("./components/RulesDialog").then(m => ({
    default: m.GlobalRulesDialog,
  }))
);
const PrerequisitePopups = lazy(() =>
  import("./components/PrerequisitePopups/PrerequisitePopups").then(m => ({
    default: m.PrerequisitePopups,
  }))
);
function App() {
  useFavoriteGames();
  useIOSKeyboardFix();

  const [deviceBlacklisted] = useGlobalValue("deviceBlacklisted");
  const { isAuthenticated, userId, username, email } = useUserDataFlattened();
  const customerServices = useCustomerServices();

  // 获取在线客服配置（必须在条件 return 之前调用，保证 Hooks 数量一致，避免 React #300）
  const onlineServiceConfig = useMemo(() => {
    const onlineService = customerServices.find(
      service => service.customerCategory === "ONLINE_SERVICE"
    );
    if (!onlineService) return null;

    const serviceType = onlineService.serviceType;
    if (serviceType === "TAWK_TO") {
      // TAWK_TO 需要 propertyId 和 widgetId
      if (onlineService.propertyId && onlineService.widgetId) {
        return {
          type: "TAWK_TO" as const,
          propertyId: onlineService.propertyId,
          widgetId: onlineService.widgetId,
        };
      }
    } else if (serviceType === "LIVECHAT") {
      // LIVE_CHAT 需要 licenseId
      if (onlineService.licenseId) {
        return {
          type: "LIVE_CHAT" as const,
          licenseId: onlineService.licenseId,
        };
      }
    }
    return null;
  }, [customerServices]);

  useEffect(() => {
    document.body.setAttribute("data-device", "mobile");

    // 真实 iOS 设备标记（电脑浏览器模拟器不会命中）
    const isRealIOS =
      ["iPad", "iPhone", "iPod"].some(d => navigator.platform?.includes(d)) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    if (isRealIOS) {
      document.body.classList.add("is-ios");
    }

    const isRealAndroid =
      /Android/i.test(navigator.userAgent) && "ontouchend" in document;
    if (isRealAndroid) {
      document.body.classList.add("is-android");
    }

    initScreenWidth();

    return () => {
      document.body.removeAttribute("data-device");
      document.body.classList.remove("is-ios");
      document.body.classList.remove("is-android");
    };
  }, []);

  // 设备被拉入黑名单时，仅展示拦截页，不展示当前页面信息（放在所有 Hooks 之后，避免 React #300）
  if (deviceBlacklisted) {
    return <BlockedDevicePage />;
  }

  // 应用主界面
  return (
    <div className="phone-wrapper">
      <AppRouter />
      <Suspense fallback={null}>
        <SilentErrorBoundary>
          {onlineServiceConfig?.type === "TAWK_TO" && (
            <TawkToChat
              propertyId={onlineServiceConfig.propertyId}
              widgetId={onlineServiceConfig.widgetId}
              hideWidget={true}
              user={
                isAuthenticated
                  ? {
                      userId: userId?.toString(),
                      name: username,
                      email: email,
                    }
                  : undefined
              }
              onLoad={() => console.log("Tawk.to loaded globally")}
            />
          )}
          {onlineServiceConfig?.type === "LIVE_CHAT" && (
            <LiveChat
              license={onlineServiceConfig.licenseId}
              hideWidget={true}
              user={
                isAuthenticated
                  ? {
                      userId: userId?.toString(),
                      name: username,
                      email: email,
                    }
                  : undefined
              }
              onLoad={() => console.log("LiveChat loaded globally")}
            />
          )}
          <GlobalSafariAddHomeScreen />
          <GlobalRulesDialog />
        </SilentErrorBoundary>
      </Suspense>
      <AgreementConfirmation />
      {/* 底部弹窗式充值 */}
      <DepositPopup />
      {/* 底部弹窗式提现 */}
      <WithdrawPopup />
      {/* PopupQueueProvider + FloatingIconsProvider 包裹 LeftBottomFloatingIcons：
          useFloatingIcons 内部业务 hooks 依赖 PopupQueue 上下文，
          Context 缺失时 useFloatingIconsContext 走空 fallback，丢失首屏左下角图标（含客服）。
          LoginRegisterPopup 在此处以便使用 pause/resume 控制弹窗队列（首页自动触发时插队）。
          LoginRegisterPopup 必须在 Suspense 边界外：若与下方 lazy 组件同边界，
          其事件监听器要等全部 chunk 加载完才挂载，首页自动触发的弹窗事件会被吞掉。 */}
      <PopupQueueProvider initialPaused>
        {/* 底部弹窗式登录注册，需在 PopupQueueProvider 内以访问 pause/resume */}
        <LoginRegisterPopup />
        <Suspense fallback={null}>
          <FloatingIconsProvider>
            <SilentErrorBoundary>
              <LeftBottomFloatingIcons />
            </SilentErrorBoundary>
            <SilentErrorBoundary>
              <PrerequisitePopups />
              <WebPush />
              <GlobalDownloadAppPopup />
              <GlobalAppDownloadDialog />
              <GlobalEvent
                {...APP_GLOBAL_EVENT_FLOATING_VISIBILITY}
                rechargeOfferVisible={false}
              />
            </SilentErrorBoundary>
          </FloatingIconsProvider>
        </Suspense>
      </PopupQueueProvider>
      <UnpaidGuideController />
    </div>
  );
}

export default App;
